import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, inject } from '@angular/core';
import { BbitWebsiteService } from '@bbitgmbh/website.sdk';
import { Subject, take } from 'rxjs';
import { BookingInfoOverlayComponent } from './booking-info-overlay/booking-info-overlay.component';
import { BookingInfoOverlayRef } from './booking-info-overlay/booking-info-overlay.ref';
import { BookingOverlayComponent } from './booking-overlay/booking-overlay.component';
import { BookingOverlayRef, IBbitBookingOverlayData } from './booking-overlay/booking-overlay.ref';
import { CreditTopUpOverlayComponent } from './credit-top-up-overlay/credit-top-up-overlay.component';
import { CreditTopUpOverlayRef } from './credit-top-up-overlay/credit-top-up-overlay.ref';

@Injectable({
  providedIn: 'root',
})
export class BookingOverlayService {
  private _overlay = inject(Overlay);
  private _breakpointObserver = inject(BreakpointObserver);
  private _website = inject(BbitWebsiteService);
  public async openInfoOverlay(): Promise<void> {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop',
      panelClass: '',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
    });
    const overlayRef = this._overlay.create(overlayConfig);

    const overlayRefInstance = new BookingInfoOverlayRef(overlayRef);
    const injector = Injector.create({
      providers: [
        { provide: BookingInfoOverlayRef, useValue: overlayRefInstance },
        { provide: BbitWebsiteService, useValue: this._website },
      ],
    });
    const containerPortal = new ComponentPortal(BookingInfoOverlayComponent, null, injector);
    overlayRef.attach(containerPortal);
    const breakpoint = this._breakpointObserver.observe(Breakpoints.XSmall).subscribe((size) => {
      if (size.matches) {
        overlayRef.updateSize({ width: '100%', height: '100%', maxHeight: '100%' });
      } else {
        overlayRef.updateSize({
          minWidth: '400px',
          maxWidth: '600px',
          width: '80%',
          height: '80%',
          maxHeight: '80%',
        });
      }
    });
    overlayRef.backdropClick().subscribe((): void => {
      breakpoint.unsubscribe();
      overlayRefInstance.close();
    });
  }

  async openCreditTopUp(): Promise<{ reload: boolean }> {
    return new Promise((resolve) => {
      const overlayConfig = new OverlayConfig({
        hasBackdrop: true,
        backdropClass: 'overlay-backdrop',
        panelClass: '',
        scrollStrategy: this._overlay.scrollStrategies.block(),
        positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
      });
      const overlayRef = this._overlay.create(overlayConfig);
      const overlayRefInstance = new CreditTopUpOverlayRef(overlayRef);
      const injector = Injector.create({
        providers: [
          { provide: CreditTopUpOverlayRef, useValue: overlayRefInstance },
          { provide: BbitWebsiteService, useValue: this._website },
        ],
      });
      const containerPortal = new ComponentPortal(CreditTopUpOverlayComponent, null, injector);
      overlayRef.attach(containerPortal);
      const breakpoint = this._breakpointObserver.observe(Breakpoints.XSmall).subscribe((size) => {
        if (size.matches) {
          overlayRef.updateSize({ width: '100%', height: '100%' });
        } else {
          overlayRef.updateSize({
            minWidth: '400px',
            maxWidth: '600px',
            width: '80%',
            height: '80%',
            maxHeight: '80%',
          });
        }
      });
      overlayRef.backdropClick().subscribe((): void => {
        breakpoint.unsubscribe();
        overlayRefInstance.close();
        resolve({ reload: false });
      });
      overlayRefInstance.observeClose().subscribe(async (reload: boolean) => {
        breakpoint.unsubscribe();
        resolve({ reload });
      });
    });
  }

  public async openBookingOverlay(params: IBbitBookingOverlayData, reloadSubject: Subject<boolean>): Promise<void> {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop',
      panelClass: '',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
    });
    const overlayRef = this._overlay.create(overlayConfig);
    const overlayRefInstance = new BookingOverlayRef(overlayRef, params, this);
    overlayRefInstance
      .observeClose()
      .pipe(take(1))
      .subscribe((reload) => {
        reloadSubject.next(reload);
        reloadSubject.complete();
      });
    const injector = Injector.create({
      providers: [
        { provide: BookingOverlayRef, useValue: overlayRefInstance },
        { provide: BbitWebsiteService, useValue: this._website },
      ],
    });
    const containerPortal = new ComponentPortal(BookingOverlayComponent, null, injector);
    overlayRef.attach(containerPortal);

    const breakpoint = this._breakpointObserver.observe(Breakpoints.XSmall).subscribe((size) => {
      if (size.matches) {
        overlayRef.updateSize({ width: '100%', height: '100%' });
      } else {
        overlayRef.updateSize({ minWidth: '400px', maxWidth: '600px', width: '80%', height: '80%', maxHeight: '80%' });
      }
    });

    overlayRef.backdropClick().subscribe((): void => {
      breakpoint.unsubscribe();
      overlayRefInstance.close();
    });
  }
}
